import { FC, useState, useMemo } from "react"
import {
  FileSearch,
  Loader2,
  CheckCircle,
  XCircle,
  ChevronDown,
  ChevronRight
} from "lucide-react"
import { PrimaryBtn } from "../../buttons"
import Tabs from "../../tabs"
import OrgAutocomplete from "../../org-autocomplete"
import diagnosticsApi, {
  InvoiceSingleResult,
  InvoiceBulkResult
} from "../../../../api/diagnostics"
import { useAppDispatch } from "../../../hooks"
import { pushToast } from "../../../../redux/slices/notifications"
import { useFetchOrgListQuery } from "../../../../redux/queries"

type TabId = "single" | "bulk"

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December"
]

const getDefaults = () => {
  const now = new Date()
  let month = now.getMonth() // 0-indexed, so this is "last month" in 1-indexed
  let year = now.getFullYear()
  if (month === 0) {
    month = 12
    year -= 1
  }
  return { month, year }
}

const InvoiceDiagnostic: FC = () => {
  const dispatch = useAppDispatch()
  const defaults = getDefaults()
  const { data: orgs } = useFetchOrgListQuery({ limit: 999 })
  const [activeTab, setActiveTab] = useState<TabId>("single")

  // Single org state
  const [orgId, setOrgId] = useState("")
  const [singleMonth, setSingleMonth] = useState(defaults.month)
  const [singleYear, setSingleYear] = useState(defaults.year)
  const [singleLoading, setSingleLoading] = useState(false)
  const [singleResult, setSingleResult] = useState<InvoiceSingleResult | null>(
    null
  )

  // Bulk state
  const [bulkMonth, setBulkMonth] = useState(defaults.month)
  const [bulkYear, setBulkYear] = useState(defaults.year)
  const [includeZero, setIncludeZero] = useState(false)
  const [excludeStripe, setExcludeStripe] = useState(false)
  const [bulkLoading, setBulkLoading] = useState(false)
  const [bulkResult, setBulkResult] = useState<InvoiceBulkResult | null>(null)
  const [expandedOrgs, setExpandedOrgs] = useState<Set<number>>(new Set())

  const tabs = [
    { id: "single" as TabId, label: "Single Org" },
    { id: "bulk" as TabId, label: "Bulk Scan" }
  ]

  const SKIP_KEYS = new Set(["month", "year", "problems", "status", "message"])

  const bulkStats = useMemo(() => {
    if (!bulkResult) return []
    return Object.entries(bulkResult)
      .filter(([key, val]) => typeof val === "number" && !SKIP_KEYS.has(key))
      .map(([key, val]) => ({
        label: key.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase()),
        value: val as number
      }))
  }, [bulkResult])

  const handleSingleRun = async () => {
    const id = parseInt(orgId, 10)
    if (!id) return
    setSingleLoading(true)
    setSingleResult(null)

    try {
      const result = await diagnosticsApi.invoiceSingle({
        org_id: id,
        month: singleMonth,
        year: singleYear
      })
      setSingleResult(result)
    } catch (error: any) {
      const msg =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to run invoice diagnostic"
      dispatch(pushToast({ msg, theme: "danger", timeout: 5000 }))
    } finally {
      setSingleLoading(false)
    }
  }

  const handleBulkRun = async () => {
    setBulkLoading(true)
    setBulkResult(null)
    setExpandedOrgs(new Set())

    try {
      const result = await diagnosticsApi.invoiceBulk({
        month: bulkMonth,
        year: bulkYear,
        include_zero: includeZero || undefined,
        exclude_has_stripe_invoice: excludeStripe || undefined
      })
      setBulkResult(result)
    } catch (error: any) {
      const msg =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to run bulk invoice scan"
      dispatch(pushToast({ msg, theme: "danger", timeout: 5000 }))
    } finally {
      setBulkLoading(false)
    }
  }

  const toggleOrg = (orgId: number) => {
    setExpandedOrgs(prev => {
      const next = new Set(prev)
      if (next.has(orgId)) next.delete(orgId)
      else next.add(orgId)
      return next
    })
  }

  return (
    <div className="bg-white border border-neutral-200 rounded-lg">
      <div className="px-6 py-5 border-b border-neutral-100 bg-neutral-50 rounded-t-lg">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center">
            <FileSearch className="w-5 h-5 text-orange-600" />
          </div>
          <div>
            <h3 className="text-base font-semibold text-gray-900">
              Invoice Diagnostic
            </h3>
            <p className="text-sm text-gray-500">
              Run invoice health checks for a single org or bulk scan
            </p>
          </div>
        </div>
      </div>

      <div className="px-6">
        <Tabs tabs={tabs} currentTab={activeTab} onChange={setActiveTab} />
      </div>

      {activeTab === "single" && (
        <div className="p-6 space-y-5">
          <div className="space-y-4">
            <div>
              <OrgAutocomplete
                selected={orgId}
                orgs={orgs ?? []}
                direction="vertical"
                onChange={setOrgId}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Month:
                </label>
                <select
                  value={singleMonth}
                  onChange={e => setSingleMonth(Number(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  {MONTHS.map((name, i) => (
                    <option key={i + 1} value={i + 1}>
                      {name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Year:
                </label>
                <input
                  type="number"
                  value={singleYear}
                  onChange={e => setSingleYear(Number(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
            </div>
          </div>

          <div className="pt-2">
            <PrimaryBtn
              onClick={handleSingleRun}
              disabled={!orgId || singleLoading}
              className="w-full justify-center"
            >
              {singleLoading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Running...
                </>
              ) : (
                "Run Diagnostic"
              )}
            </PrimaryBtn>
          </div>

          {singleResult && (
            <div className="border border-neutral-200 rounded-lg overflow-hidden">
              <div className="px-4 py-3 bg-gray-50 border-b border-neutral-200">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium text-gray-900">
                    {singleResult.org_name}{" "}
                    <span className="text-gray-500">
                      (ID: {singleResult.org_id})
                    </span>
                  </p>
                  <span
                    className={`text-sm font-medium ${
                      singleResult.summary.failed === 0
                        ? "text-green-600"
                        : "text-red-600"
                    }`}
                  >
                    {singleResult.summary.passed}/{singleResult.summary.total}{" "}
                    checks passed
                  </span>
                </div>
              </div>
              <div className="divide-y divide-neutral-100">
                {singleResult.checks.map((check, i) => (
                  <div key={i} className="px-4 py-3 flex items-start gap-3">
                    {check.passed ? (
                      <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                    ) : (
                      <XCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                    )}
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        {check.check}
                      </p>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {check.diagnosis}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === "bulk" && (
        <div className="p-6 space-y-5">
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Month:
                </label>
                <select
                  value={bulkMonth}
                  onChange={e => setBulkMonth(Number(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  {MONTHS.map((name, i) => (
                    <option key={i + 1} value={i + 1}>
                      {name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Year:
                </label>
                <input
                  type="number"
                  value={bulkYear}
                  onChange={e => setBulkYear(Number(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={includeZero}
                  onChange={e => setIncludeZero(e.target.checked)}
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                Include $0 invoices
              </label>
              <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={excludeStripe}
                  onChange={e => setExcludeStripe(e.target.checked)}
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                Exclude orgs with Stripe invoice
              </label>
            </div>
          </div>

          <div className="pt-2">
            <PrimaryBtn
              onClick={handleBulkRun}
              disabled={bulkLoading}
              className="w-full justify-center"
            >
              {bulkLoading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Scanning...
                </>
              ) : (
                "Scan All Orgs"
              )}
            </PrimaryBtn>
          </div>

          {bulkResult && (
            <div className="border border-neutral-200 rounded-lg overflow-hidden">
              <div className="px-4 py-3 bg-gray-50 border-b border-neutral-200">
                <div
                  className="grid gap-3 text-center"
                  style={{
                    gridTemplateColumns: `repeat(${bulkStats.length}, minmax(0, 1fr))`
                  }}
                >
                  {bulkStats.map(({ label, value }) => (
                    <div key={label}>
                      <p className="text-lg font-semibold text-gray-900">
                        {value}
                      </p>
                      <p className="text-xs text-gray-500">{label}</p>
                    </div>
                  ))}
                </div>
              </div>

              {bulkResult.problems.length > 0 ? (
                <div className="divide-y divide-neutral-100">
                  <div className="px-4 py-2 bg-red-50">
                    <p className="text-sm font-medium text-red-700">
                      {bulkResult.problems.length} org
                      {bulkResult.problems.length !== 1 ? "s" : ""} with issues
                    </p>
                  </div>
                  {bulkResult.problems.map(org => (
                    <div key={org.org_id}>
                      <button
                        onClick={() => toggleOrg(org.org_id)}
                        className="w-full px-4 py-3 flex items-center gap-2 text-left hover:bg-gray-50 transition-colors"
                      >
                        {expandedOrgs.has(org.org_id) ? (
                          <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />
                        ) : (
                          <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
                        )}
                        <span className="text-sm font-medium text-gray-900">
                          {org.org_name}
                        </span>
                        <span className="text-xs text-gray-500">
                          (ID: {org.org_id})
                        </span>
                        <span className="ml-auto text-xs text-red-500">
                          {org.issues.length} issue
                          {org.issues.length !== 1 ? "s" : ""}
                        </span>
                      </button>
                      {expandedOrgs.has(org.org_id) && (
                        <div className="px-4 pb-3 pl-10 space-y-1">
                          {org.issues.map((issue, i) => (
                            <div
                              key={i}
                              className="flex items-start gap-2 text-sm text-gray-700"
                            >
                              <XCircle className="w-3.5 h-3.5 text-red-400 mt-0.5 flex-shrink-0" />
                              <span>{issue}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="px-4 py-6 text-center">
                  <CheckCircle className="w-8 h-8 text-green-500 mx-auto mb-2" />
                  <p className="text-sm text-gray-600">
                    No issues found across all orgs
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default InvoiceDiagnostic
